import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '../PageContainer';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';
import Box from '@mui/material/Box/Box';
import Button from '@mui/material/Button/Button';
import Card from '@mui/material/Card/Card';
import CardContent from '@mui/material/CardContent/CardContent';


import { AppBreadcrumbs, PageType } from '../Breadcrumbs/Buttons';
import { GlassBox, H1, H2 } from '../ui';
import { useCrews, useCrewInvitations } from '../../hooks/useCrews';
import { CrewDTO, User } from '../../../../shared/types';

import CardActions from '@mui/material/CardActions';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { useAuth } from '../../hooks/useAuth';
import { logout } from '../../services/auth.service';
import axiosService from '../../services/axios.service';
import Alert from '@mui/material/Alert';
import { PENDING_CREW_KEY, type PendingCrewSetup } from './PreAuthCrewSetupDialog';
import DefaultAppHeader from '../AppHeader/DefaultAppHeader';
import defaultCrewHeaderImage from '../../assets/default_crew_header.jpg';
import JoinWithInviteCode from './JoinWithInviteCode';
import { BrowsePublicCrews } from './BrowsePublicCrews';
import Container from '@mui/material/Container/Container';

interface AuthenticatedHomeProps {
    user: User;
}

const AuthenticatedHome = ({ user }: AuthenticatedHomeProps) => {
    const navigate = useNavigate();
    const { data, isLoading } = useCrews(1, 10);
    const userCrews = data?.data || [];

    // ── Pending crew: created before auth, finish setup now ──────────────────
    const pendingHandled = useRef(false);
    const [pendingCreating, setPendingCreating] = useState(false);
    const [pendingError, setPendingError] = useState<string | null>(null);

    useEffect(() => {
        if (pendingHandled.current) return;
        pendingHandled.current = true;

        const raw = localStorage.getItem(PENDING_CREW_KEY);
        if (!raw) return;

        let pending: PendingCrewSetup;
        try {
            pending = JSON.parse(raw);
        } catch {
            localStorage.removeItem(PENDING_CREW_KEY);
            return;
        }

        setPendingCreating(true);
        axiosService.post('/c', { name: pending.name, description: pending.description, websiteUrl: pending.website })
            .then((res) => {
                localStorage.removeItem(PENDING_CREW_KEY);
                const slug = (res.data as any).slug;
                const params = new URLSearchParams({ welcome: 'true' });
                if (pending.website) params.set('website', pending.website);
                navigate(`/c/${slug}?${params}`);
            })
            .catch((err) => {
                localStorage.removeItem(PENDING_CREW_KEY);
                const msg = err?.response?.data?.error ?? 'Failed to create your crew. Please try again from the home page.';
                setPendingError(msg);
                setPendingCreating(false);
            });
    }, []);
    // ─────────────────────────────────────────────────────────────────────────

    // If loading, show a loading state
    if (pendingCreating || isLoading) {
        return (
            <Container>
                <GlassBox sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                    <CircularProgress color='secondary' />
                </GlassBox>
            </Container>
        );
    }

    const pendingAlert = pendingError && (
        <Alert severity="warning" onClose={() => setPendingError(null)} sx={{ mb: 1 }}>
            {pendingError}
        </Alert>
    );

    // If user has multiple crews, show crew selection
    if (userCrews && userCrews.length > 0) {
        return (
            <>
                <DefaultAppHeader />
                <PageContainer className="home" sx={{ py: 2 }}>
                    <Stack spacing={3} pb={2} direction='column'>
                        {pendingAlert}
                        <AppBreadcrumbs crumbs={[{ type: PageType.Home, name: 'Home' }]} />
                        <CrewGrid crews={userCrews} user={user} />
                        <CrewInvitations />
                        <BrowsePublicCrews />
                    </Stack>
                </PageContainer>
            </>
        );
    }

    // If user has no crews, show onboarding option alongside invitations
    return (
        <>
            <DefaultAppHeader />
            <PageContainer className="home">
                <Stack spacing={3} pb={2} direction='column'>
                    {pendingAlert}
                    <NoCrewsState />
                    <CrewInvitations />
                    <BrowsePublicCrews />
                </Stack>
            </PageContainer>
        </>

    );
};

const CrewGrid = ({ crews, user }: { crews: CrewDTO[]; user: User }) => {
    const navigate = useNavigate();

    return (
        <Box>
            <Box>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Box>
                            <H1 sx={{ mb: 1 }}>Your Crews</H1>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Stack direction="row" spacing={2} justifyContent={{ xs: 'center', md: 'flex-end' }}>
                            {user?.isAdmin && (
                                <Button variant="outlined" onClick={() => navigate('/admin')} fullWidth>
                                    Admin Dashboard
                                </Button>
                            )}
                            <Button variant="contained" onClick={() => navigate('/onboarding')} fullWidth>
                                Create New Crew
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
            <Box>
                <Grid container spacing={3}>
                    {crews.map((crew) => (
                        <Grid item xs={12} sm={6} md={4} key={crew.slug}>
                            <Card
                                sx={{
                                    height: '100%',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    '&:hover': { elevation: 4 }
                                }}
                                onClick={() => navigate(`/c/${crew.slug}`)}
                            >
                                <Box
                                    component="img"
                                    src={crew.headerImage || defaultCrewHeaderImage}
                                    alt={crew.name}
                                    sx={{
                                        width: '100%',
                                        height: 160,
                                        objectFit: 'cover'
                                    }}
                                />
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} mb={1}>
                                        <Typography variant="h6" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
                                            {crew.name}
                                        </Typography>
                                        <Chip label={`c/${crew.slug}`} size="small" variant="outlined" sx={{ flexShrink: 0 }} />
                                    </Box>
                                    {crew.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            {crew.description}
                                        </Typography>
                                    )}
                                </CardContent>
                                <CardActions>
                                    <Button size="small" fullWidth>
                                        Enter Crew
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Box >
    );
};

const CrewInvitations = () => {
    const navigate = useNavigate();
    const { data: invitedCrews } = useCrewInvitations();

    return (
        <Box>
            <H2 sx={{ mb: 3 }}>Join a Crew</H2>
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Have an invite code?
                    </Typography>
                    <JoinWithInviteCode />
                </CardContent>

                {invitedCrews && invitedCrews.length > 0 && (
                    <>
                        <Divider />
                        <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                You've been added to the following crews
                            </Typography>
                            <Grid container spacing={2}>
                                {invitedCrews.map((crew) => (
                                    <Grid item xs={12} sm={6} md={4} key={crew.slug}>
                                        <Card
                                            sx={{ cursor: 'pointer', display: 'flex', flexDirection: 'column' }}
                                            onClick={() => navigate(`/c/${crew.slug}`)}
                                        >
                                            <Box
                                                component="img"
                                                src={crew.headerImage || defaultCrewHeaderImage}
                                                alt={crew.name}
                                                sx={{ width: '100%', height: 120, objectFit: 'cover' }}
                                            />
                                            <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                                                <Typography variant="subtitle1">{crew.name}</Typography>
                                                {crew.description && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {crew.description}
                                                    </Typography>
                                                )}
                                            </CardContent>
                                            <CardActions>
                                                <Button size="small" variant="contained" fullWidth>
                                                    Join Crew
                                                </Button>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </CardContent>
                    </>
                )}
            </Card>
        </Box>
    );
};

const NoCrewsState = () => {
    const navigate = useNavigate();
    const { user, loading } = useAuth();
    if (loading) {
        return <CircularProgress />;
    }
    if (!user) {
        return null;
    }
    return (
        <Stack spacing={2}>
            <div className="hero-image"></div>
            <Box textAlign="center" py={4}>
                <H2 sx={{ mb: 2 }}>Aloha {user.username}, welcome to Wayfinder!</H2>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
                    You're not part of any crews yet. Create your first crew to start building stronger teams through mentorship and collaboration.
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="center">
                    <Button
                        variant="contained"
                        onClick={() => navigate('/onboarding')}
                        size="large"
                    >
                        Create Your First Crew
                    </Button>
                </Stack>
                <Stack py={2} direction="row" justifyContent={'center'} alignItems={'center'}>
                    Not you? <Button onClick={() => {
                        logout();
                    }}>Switch account</Button>
                </Stack>
            </Box>
        </Stack>
    );
};

export default AuthenticatedHome;
